<template>
  <div class="tornado-container" :class="{ 'active': isActive }">
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  isActive: {
    type: Boolean,
    default: false
  }
});

const canvas = ref(null);
let ctx = null;
let animationFrameId = null;
let particles = [];
const particleCount = 800;
let w = 0;
let h = 0;

// 更复杂的龙卷风逻辑
class TornadoParticle {
  constructor() {
    this.reset(true);
  }

  reset(initial = false) {
    this.y = initial ? Math.random() * h : h + 10; // 从底部产生，或者随机分布
    this.angle = Math.random() * Math.PI * 2;
    
    // 初始半径（底部较小）
    this.baseRadius = Math.random() * 20; 
    
    this.speedY = Math.random() * 5 + 2; // 上升速度
    this.speedAngle = 0.1 + Math.random() * 0.1; // 旋转角速度
    
    this.size = Math.random() * 3 + 1;
    this.opacity = Math.random() * 0.6 + 0.2;
    this.color = Math.random() > 0.8 ? 'rgba(100, 200, 255,' : 'rgba(255, 255, 255,';
  }

  update() {
    this.y -= this.speedY;
    this.angle += this.speedAngle;
    
    // 计算当前高度对应的最大半径（倒圆锥，越往上越大）
    const heightProgress = 1 - (this.y / h); // 0(底) -> 1(顶)
    // 曲线控制形状，用幂函数让它底部更细
    const currentRadiusSpread = 20 + Math.pow(heightProgress, 2) * (Math.min(w, h) * 0.5);
    
    const r = currentRadiusSpread + (Math.random() - 0.5) * 20; // 加上随机扰动
    
    this.x = w / 2 + Math.cos(this.angle) * r;
    // 增加一点透视感或Y轴扰动
    
    if (this.y < -50) {
      this.reset();
    }
  }

  draw() {
    ctx.beginPath();
    ctx.fillStyle = `${this.color}${this.opacity})`;
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fill();
  }
}

const initParticles = () => {
  particles = [];
  for (let i = 0; i < particleCount; i++) {
    particles.push(new TornadoParticle());
  }
};

const animate = () => {
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  
  // 绘制一些背景风线增强动感
  
  particles.forEach(p => {
    p.update();
    p.draw();
  });
  
  if (props.isActive) {
    animationFrameId = requestAnimationFrame(animate);
  }
};

const handleResize = () => {
  if (canvas.value) {
    w = window.innerWidth;
    h = window.innerHeight;
    canvas.value.width = w;
    canvas.value.height = h;
    // 不重置粒子，让它们自然适应
  }
};

watch(() => props.isActive, (val) => {
  if (val) {
    handleResize();
    initParticles();
    animate();
  } else {
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
    if (ctx) ctx.clearRect(0, 0, w, h);
  }
});

onMounted(() => {
  if (canvas.value) {
    ctx = canvas.value.getContext('2d');
    handleResize();
    window.addEventListener('resize', handleResize);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  if (animationFrameId) cancelAnimationFrame(animationFrameId);
});
</script>

<style scoped>
.tornado-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.5s ease;
  background: rgba(0, 0, 0, 0.2); /* 稍微变暗背景 */
  backdrop-filter: blur(2px);
}

.tornado-container.active {
  opacity: 1;
  pointer-events: all; /* 阻挡点击 */
}
</style>
